import React from 'react'
import { StyleProp, View, ViewStyle } from 'react-native'
import { useTheme } from '@ui-kitten/components'
import { useSelector } from 'react-redux'
import { useNavigation } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { StyleService, useStyleSheet } from '@src/style/service'
import { TouchableSection } from '@src/components'
import { Feature, useFeatureFlag } from '@src/components/FeatureFlag'
import { Icon } from '@src/components/base/Icon'
import { RootStackParamList } from '@src/navigation/types'
import { userSelector } from '@src/selectors/app'
import { useIsDarkMode } from '@src/config/theme'
import { SurveysConfigKind, UserPropertyOnboardingExperienceKind } from '@src/types'

interface UhcOnboardingBannerProps {
  style?: StyleProp<ViewStyle>
}

export const UhcOnboardingBanner = ({ style }: UhcOnboardingBannerProps) => {
  const theme = useTheme()
  const styles = useStyleSheet(themedStyle)
  const isDarkMode = useIsDarkMode()
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>()
  const notificationInboxEnabled = useFeatureFlag(Feature.NotificationInbox)

  const user = useSelector(userSelector)
  const onboardingExperience = user?.onboardingExperience
  const userIsWeightLossParticipant =
    onboardingExperience?.kind === UserPropertyOnboardingExperienceKind.WeightLoss &&
    onboardingExperience.participating

  // The store user can be partially hydrated without surveyLinks at runtime,
  // even though the type declares the field as always present.
  const coachingGoalsSurvey = (user?.surveyLinks ?? []).find(
    (survey) => survey.survey.kind === SurveysConfigKind.CoachingGoalsAndPreferences,
  )

  const shouldShowBanner =
    !userIsWeightLossParticipant && !!user?.uhcMember && !coachingGoalsSurvey?.finished

  if (notificationInboxEnabled) {
    return null
  }

  if (!shouldShowBanner) {
    return null
  }

  const handleOnPress = () => {
    navigation.navigate('Questionnaire', {
      questionnaire: SurveysConfigKind.CoachingGoalsAndPreferences,
    })
    return
  }

  const textColor = isDarkMode ? theme['theme.tooltip.textPrimary'] : theme['theme.text.primary']
  const backgroundColor = isDarkMode
    ? theme['theme.marketing.yellow']
    : theme['theme.marketing.lightYellow']
  const iconBackgroundColor = isDarkMode
    ? theme['theme.marketing.lightYellow']
    : theme['theme.marketing.lighterYellow']
  const rightArrowColor = isDarkMode ? theme['theme.solid.black'] : undefined

  return (
    <TouchableSection
      title="Before your initial call"
      subtitle="Fill out the onboarding survey to personalize your experience"
      onPress={handleOnPress}
      style={[styles.content, { backgroundColor }, style]}
      subtitleStyle={[styles.subtitle, { color: textColor }]}
      labelStyle={{ color: textColor }}
      accessoryLeft={
        <View style={[styles.iconContainer, { backgroundColor: iconBackgroundColor }]}>
          <Icon name="bell-ringing" size={26} weight="bold" color={textColor} />
          <Icon name="circle" style={styles.unreadMessageDot} weight="fill" />
        </View>
      }
      showRightArrow
      rightArrowColor={rightArrowColor}
    />
  )
}

const themedStyle = StyleService.create({
  content: {
    borderRadius: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 16,
  },
  iconContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 16,
    borderRadius: 28,
    height: 56,
    width: 56,
  },
  subtitle: {
    maxWidth: 246,
  },
  unreadMessageDot: {
    position: 'absolute',
    top: 4,
    right: -4,
    width: 16,
    height: 16,
    color: 'theme.error.base',
  },
})
